import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import colors from "../../../config/colors";
import {
	EnabledFeatures,
	QueueItemType,
	SupervisorQueueItem,
} from "../store/supervisor-home-types";
import {
	formatQueueItemDuration,
	formatQueueItemWhen,
	getOpenShiftSpots,
} from "../utils/queue-item-format";
import { getQueueItemUrl } from "../utils/queue-navigation";
import { HOME_LINKS } from "../utils/queue-navigation";

export type StatusTone = "error" | "warning" | "info" | "neutral";

export interface QueueRow {
	id: string;
	type: QueueItemType;
	accentColor: string;
	when: string;
	duration: string;
	primary: string;
	secondary: string;
	statusLabel: string;
	statusTone: StatusTone;
	actionLabel: string;
	url: string;
}

export interface QueueGroup {
	type: QueueItemType;
	title: string;
	countLabel: string;
	linkLabel: string;
	url: string;
	rows: QueueRow[];
}

const GROUP_ORDER: QueueItemType[] = ["openShift", "shiftSwap", "timeOff"];

const useSupervisorQueue = (
	queue?: SupervisorQueueItem[],
	enabledFeatures?: EnabledFeatures
) => {
	const { t } = useTranslation();

	return useMemo(() => {
		const items = queue ?? [];
		const today = new Date();

		const buildRow = (item: SupervisorQueueItem): QueueRow => {
			const shared = {
				id: `${item.type}-${item.sourceId}`,
				type: item.type,
				when: formatQueueItemWhen(item, today),
				duration: formatQueueItemDuration(item, t),
				url: getQueueItemUrl(item.type),
				// Rows only navigate, and the destination page decides what the
				// supervisor may actually do there, so never promise an action.
				actionLabel: t("supervisor-home_action-review"),
			};
			const place = [item.jobName, item.workCenterName]
				.filter(Boolean)
				.join(" · ");

			if (item.type === "openShift") {
				const { filled, needed, remaining } = getOpenShiftSpots(item);
				const spotsLeft =
					remaining === 1
						? t("supervisor-home_status-one-spot-left")
						: t("supervisor-home_status-spots-left", { count: remaining });

				return {
					...shared,
					accentColor: item.isUrgent ? colors.error : colors.warningColor,
					primary: place,
					secondary: t("supervisor-home_spots-filled", { filled, needed }),
					statusLabel: item.isUrgent
						? t("supervisor-home_status-urgent")
						: spotsLeft,
					statusTone: item.isUrgent ? "error" : "warning",
				};
			}

			return {
				...shared,
				accentColor:
					item.type === "shiftSwap" ? colors.infoIcon : colors.selection,
				primary: item.workerName ?? place,
				secondary: item.workerName ? place : "",
				statusLabel: t("supervisor-home_status-pending"),
				statusTone: "info",
			};
		};

		const meta: Record<
			QueueItemType,
			{ title: string; linkLabel: string; url: string }
		> = {
			openShift: {
				title: t("supervisor-home_group-open-shifts"),
				linkLabel: t("supervisor-home_go-to-flex-schedule"),
				url: HOME_LINKS.flexSchedule,
			},
			shiftSwap: {
				title: t("supervisor-home_group-shift-swaps"),
				linkLabel: t("supervisor-home_go-to-worker-requests"),
				url: HOME_LINKS.flexShiftRequests,
			},
			timeOff: {
				title: t("supervisor-home_group-time-off"),
				linkLabel: t("supervisor-home_go-to-worker-requests"),
				url: HOME_LINKS.timeOffRequests,
			},
		};

		// A feature turned off still returns its queue items as an empty list,
		// but guard anyway so a stale payload can't surface a hidden section.
		const isTypeEnabled = (type: QueueItemType): boolean => {
			if (type === "shiftSwap") {
				return enabledFeatures?.shiftSwap !== false;
			}
			if (type === "timeOff") {
				return enabledFeatures?.attendanceManagement !== false;
			}
			return true;
		};

		const groups: QueueGroup[] = GROUP_ORDER.filter(isTypeEnabled)
			.map((type) => {
				const rows = items.filter((item) => item.type === type).map(buildRow);
				return {
					type,
					title: meta[type].title,
					linkLabel: meta[type].linkLabel,
					url: meta[type].url,
					// Open shifts are today-only on this endpoint, so label them
					// that way instead of letting the list read as forward-looking.
					countLabel:
						type === "openShift"
							? t("supervisor-home_group-count-today-only", {
								count: rows.length,
							})
							: t("supervisor-home_group-count", { count: rows.length }),
					rows,
				};
			})
			.filter((group) => group.rows.length > 0);

		const totalCount = groups.reduce(
			(sum, group) => sum + group.rows.length,
			0
		);

		return { groups, totalCount, isEmpty: totalCount === 0 };
	}, [queue, enabledFeatures, t]);
};

export default useSupervisorQueue;
